import React from "react";
import { Box, Typography, Button, useTheme, useMediaQuery } from "@mui/material";
import SafeLifeEveryMonth from "../../../components/common/SafeLifeEveryMonth";
import { useDispatch, useSelector } from "react-redux";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";

const MonthlyDonationHero = () => {
  const theme = useTheme();
  // Custom media query for screens 900px to 1200px
  const isMediumScreen = useMediaQuery('(min-width: 900px) and (max-width: 1200px)');
  
  // Custom media query for screens below 900px
  const isSmallScreen = useMediaQuery('(max-width: 899px)');

  // Custom media query for mobile screens (600px and below)
  const isMobile = useMediaQuery('(max-width: 600px)');

  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);

  React.useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const { heroTitle, heroSubtitle, heroImage } = React.useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const sec = data.find((s) => s?.id === 82);
    return {
      heroTitle: sec?.title || '',
      heroSubtitle: sec?.sub_title || '',
      heroImage: sec?.image || '',
    };
  }, [cmsHome]);

  // Responsive styles function
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: {
          height: '203px', // Reduced height for mobile
          width: '94.44%', // 94.44% width for image section
        },
        contentContainer: {
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-end',
          textAlign: 'left',
          padding: '20px' // 20px padding for image
        },
        leftSection: {
          maxWidth: '100%', // Full width for mobile
          textAlign: 'left'
        },
        title: {
          fontSize: '18px',
          lineHeight: '29.12px',
          letterSpacing: '-1%',
          marginBottom: '8px'
        },
        description: {
          fontSize: '14px',
          lineHeight: '22px',
          letterSpacing: '-2%',
          marginBottom: '12px'
        },
        button: {
          width: isMobile?"181px":'126px',
          height: '42px',
          borderRadius: '10px',
          paddingTop: '14px',
          paddingBottom: '14px',
          boxShadow: '0px 3.72px 0px 0px #141414',
          marginBottom: '0px'
        },
        buttonText: {
          fontSize: '14px',
          lineHeight: '14px',
          letterSpacing: '0%',
          textTransform: 'capitalize'
        },
        rightSection: {
          display: 'none' 
        }
      };
    } else if (isSmallScreen) {
      return {
        container: {
          height: '400px', 
          width: '94.44%', 
        },
        contentContainer: {
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-end',
          textAlign: 'left',
          padding: '20px'
        },
        leftSection: {
          maxWidth: '100%', 
          textAlign: 'left'
        },
        title: {
          fontSize: '32px', 
          lineHeight: '120%',
          marginBottom: '16px'
        },
        description: {
          fontSize: '14px', 
          lineHeight: '24px',
          marginBottom: '20px'
        },
        button: {
          width: '180px', 
          height: '44px',
          marginBottom: '0px'
        },
        buttonText: {
          fontSize: '14px',
          lineHeight: '14px',
          letterSpacing: '0%',
          textTransform: 'capitalize'
        },
        rightSection: {
          display: 'none' 
        }
      };
    } else if (isMediumScreen) {
      return {
        container: {
          height: '640px', 
        },
        contentContainer: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-end'
        },
        leftSection: {
          maxWidth: '350px', 
        },
        title: {
          fontSize: '50px', 
          lineHeight: '120%',
          marginBottom: '24px'
        },
        description: {
          fontSize: '16px', 
          lineHeight: '28px', 
          marginBottom: '24px'
        },
        button: {
          width: '180px', 
          height: '46px', 
          marginBottom: '16px'
        },
        buttonText: {
          fontSize: '14px',
          lineHeight: '14px',
          letterSpacing: '0%',
          textTransform: 'capitalize'
        },
        rightSection: {
          marginRight: '16px' 
        }
      };
    } else {
      return {
        container: {
          height: '640px',
        },
        contentContainer: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-end'
        },
        leftSection: {
          maxWidth: '463px',
        },
        title: {
          fontSize: '72px',
          lineHeight: '120%',
          marginBottom: '30px'
        },
        description: {
          fontSize: '18px',
          lineHeight: '30px',
          marginBottom: '30px'
        },
        button: {
          width: '196px',
          height: '50px',
          marginBottom: '20px'
        },
        buttonText: {
          fontSize: '14px',
          lineHeight: '14px',
          letterSpacing: '0%',
          textTransform: 'capitalize'
        },
        rightSection: {
          marginRight: '20px'
        }
      };
    }
  };

  const styles = getResponsiveStyles();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          width: '94.44%',
          height: styles.container.height,
          borderRadius: isSmallScreen ? '16px' : '16px',
          position: 'relative',
          overflow: 'hidden',
          margin: '0 auto',
          marginBottom: isSmallScreen ? '0px' : '80px',
          padding: styles.container.padding
        }}
      >
      {/* Background Image */}
      {heroImage ? (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          transform: 'scaleX(-1)', // Flip the image horizontally
          zIndex: 1
        }}
      />
      ) : null}
      
      {/* Gradient Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(93.72deg, #141414 7.86%, rgba(20, 20, 20, 0) 60.82%)',
          zIndex: 2
        }}
      />      
      {/* Content Container */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 3,
          height: '100%',
          display: 'flex',
          ...styles.contentContainer,
          padding: isSmallScreen ? '20px' : '40px 0px 0px 40px'
        }}
      >
        {/* Left Section - Text and Button */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: styles.leftSection.maxWidth,
            marginBottom: isMobile ? '0px' : '20px',
            textAlign: styles.leftSection.textAlign
          }}
        >
          {/* Text Content */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: styles.title.fontSize,
              lineHeight: styles.title.lineHeight,
              letterSpacing: styles.title.letterSpacing,
              color: '#FFFFFF',
              marginBottom: styles.title.marginBottom,
              textTransform: 'capitalize',
              verticalAlign: 'middle'
            }}
          >
            {heroTitle}
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              fontWeight: 400,
              fontSize: styles.description.fontSize,
              lineHeight: styles.description.lineHeight,
              letterSpacing: styles.description.letterSpacing,
              color: '#ffffffd2',
              marginBottom: styles.description.marginBottom,
              verticalAlign: 'middle'
            }}
          >
            {heroSubtitle}
          </Typography>
          
          {/* Button */}
          <Button
            variant="contained"
            onClick={() => {
              const element = document.getElementById('monthly-donation-programs');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            sx={{
              width: styles.button.width,
              height: styles.button.height,
              borderRadius: styles.button.borderRadius,
              paddingTop: styles.button.paddingTop,
              paddingBottom: styles.button.paddingBottom,
              background: '#F04951',
              boxShadow: styles.button.boxShadow,
              marginBottom: styles.button.marginBottom,
              alignSelf: 'flex-start',
              '& .MuiButton-root': {
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: '14px',
                letterSpacing: '0%',
                textAlign: 'center',
                verticalAlign: 'middle',
                color: '#FFFFFF'
              }
            }}
          >
            <Box
              sx={{        
                fontWeight: 700,
                fontSize: styles.buttonText.fontSize,
                lineHeight: styles.buttonText.lineHeight,
                letterSpacing: styles.buttonText.letterSpacing,
                textAlign: 'center',
                color: '#FFFFFF',
                textTransform: styles.buttonText.textTransform
              }}
            >
              {isMobile ? 'Donate Monthly' : 'Donate Monthly Now'}
            </Box>
          </Button>
        </Box>
        
        {/* Right Section - SafeLifeEveryMonth card */}
        {!isSmallScreen && (
          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'flex-end',
              marginBottom: '20px',
              marginRight: styles.rightSection.marginRight
            }}
          >
            <SafeLifeEveryMonth isMediumScreen={isMediumScreen} isSmallScreen={false} />
          </Box>
        )}
      </Box>
      </Box>
      
      {/* SafeLifeEveryMonth component for small screens */}
      {isSmallScreen && (
        <Box
          sx={{
            width: '100%',
            padding: '20px 16px',
            backgroundColor: '#FFFFFF'
          }}
        >
          <SafeLifeEveryMonth isMediumScreen={false} isSmallScreen={true} />
        </Box>
      )}
    </Box>
  );
};

export default MonthlyDonationHero;
